import styled from "@emotion/styled"
import { css, keyframes } from "@emotion/react"
import Typography from "@mui/material/Typography"
import { Link } from "utils"
import { breakpoints } from "../../theme/mediaQueries"
import { sizes } from "../../theme/sizes"
import { colors } from "../../theme/colors"
import { richTextListStyles } from "../../common/richTextListStyles"

/* EGDE-504 (r6): "held" is a PAUSED active bar holding full fill so the
   current slide stays marked. There is deliberately no state for other
   slides beyond idle — a full bar on a non-active slide is exactly the
   "pager of image 1 in fill when it is focused on image 2" QA returned,
   and the prototype only ever fills the active bar. */
export type NavBarState = "idle" | "active" | "held"

const fillProgress = keyframes`
  from { transform: scaleX(0); }
  to { transform: scaleX(1); }
`

export const Root = styled.div`
  position: relative;
  width: 100%;
  overflow: hidden;
  aspect-ratio: 375 / 733;

  /* EGDE-479: between md and lg the phone ratio blew the band up to ~2×
     the viewport (a 1200px-wide window got a 2300px-tall band) — and with
     the copy centred, the first screenfuls of the band were pure empty
     orange. In that range the band hugs its stacked content instead (the
     ContentLayer switches to in-flow below); real phones and desktop keep
     their design ratios. */
  @media (min-width: ${breakpoints.md}px) and (max-width: ${breakpoints.lg -
    1}px) {
    aspect-ratio: auto;
  }

  @media (min-width: ${breakpoints.lg}px) {
    aspect-ratio: 1440 / 800;
  }
`

export const SwiperWrapper = styled.div`
  position: absolute;
  inset: 0;
  z-index: 0;

  .swiper {
    padding: 0 !important;
    width: 100%;
    height: 100%;
  }

  .swiper-slide {
    width: 100% !important;
    height: 100% !important;
  }
`

export const SlideMediaWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`

/* EGDE-78: desktop/mobile media wrappers — only used when a slide has a
   dedicated mobile background; otherwise a single universal media renders. */
export const DesktopMedia = styled.div`
  position: absolute;
  inset: 0;
  display: none;

  @media (min-width: ${breakpoints.lg}px) {
    display: block;
  }
`

export const MobileMedia = styled.div`
  position: absolute;
  inset: 0;
  display: block;

  @media (min-width: ${breakpoints.lg}px) {
    display: none;
  }
`

export const SlideVideo = styled.video`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`

export const Overlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 1;
  background: rgba(0, 0, 0, 0.5);
  pointer-events: none;

  @media (min-width: ${breakpoints.lg}px) {
    background: rgba(0, 0, 0, 0.4);
  }
`

export const ContentLayer = styled.div`
  position: absolute;
  inset: 0;
  z-index: 2;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  /* EGDE-479: in the md–lg range the band's height comes from the content
     (see Root), so the layer must be in-flow to size its parent. */
  @media (min-width: ${breakpoints.md}px) and (max-width: ${breakpoints.lg -
    1}px) {
    position: relative;
    inset: auto;
  }

  /* EGDE-665 reverses the EGDE-479 desktop override that pinned the copy to
     the band's top ("the information is at the top of the banner; the
     container needs to be in the middle of the banner"), so desktop simply
     keeps the centring above.
     479's own fault — copy sitting below the fold while the band scrolls in —
     was really the md–lg range, where the phone aspect-ratio inflated the band
     to ~2x the viewport; that is fixed at the source on Root (aspect-ratio:
     auto there). On desktop the 1440/800 band is only a little taller than the
     viewport, so centred copy is within ~80px of where flex-start put it. */
  /* EGDE-318: the band scrolls in showing only its darkened background —
     the X outline, copy, CTA and carousel nav all fade in together as the
     band fills the viewport (both prototype recordings). Scrubbed via
     --scrub (set on Root), reversible; defaults to 1 so nothing is lost
     without JS. The dark Overlay stays static, exactly like the prototype. */
  opacity: var(--scrub, 1);
  /* EGDE-479: an invisible CTA must not be clickable — the scrub callback
     flips this to auto once the copy is at least half formed. Defaults to
     auto so nothing is lost without JS / with reduced motion. */
  pointer-events: var(--content-clicks, auto);

  @media (prefers-reduced-motion: reduce) {
    opacity: 1;
    pointer-events: auto;
  }
`

export const MainContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${sizes.spacing[40]}px;
  padding: ${sizes.spacing[48]}px ${sizes.spacing[16]}px;
  width: 100%;

  /* EGDE-510 point 8: the copy and the decorative X travel in from OPPOSITE
     edges, so both need the direction sign. Transforms are not mirrored by
     stylis-plugin-rtl, so it has to come from the dir attribute (same contract
     as ValuePropositionBanner and FormShell). */
  --edge: 1;

  [dir="ltr"] & {
    --edge: -1;
  }

  @media (min-width: ${breakpoints.lg}px) {
    flex-direction: row;
    gap: ${sizes.spacing[64]}px;
    padding: ${sizes.spacing[64]}px ${sizes.spacing[120]}px;
    max-width: 1440px;
    margin: 0 auto;
  }
`

export const DecorativeImage = styled.img`
  width: 343px;
  height: 343px;
  border-radius: ${sizes.borderRadius[24]}px;
  flex-shrink: 0;
  object-fit: contain;
  /* Mobile column: move X above content (JSX has content first for RTL row) */
  order: -1;

  /* EGDE-510 point 8: "Logo from left to right" — the decorative X converges
     from the inline-END edge while the copy comes from the inline-START edge
     (see ContentContainer). It used to hold still and only fade. */
  transform: translateX(
    calc(var(--edge, 1) * min(22vw, 420px) * (var(--scrub, 1) - 1))
  );

  @media (prefers-reduced-motion: reduce) {
    transform: none;
  }

  @media (min-width: ${breakpoints.lg}px) {
    order: 0;
    width: 540px;
    height: 540px;
    border-radius: 0;
  }
`

export const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${sizes.spacing[40]}px;
  width: 100%;

  /* EGDE-510 point 8 ("Section Banner"). The band only ever faded, so the copy
     had no entrance of its own; an earlier round gave it a 40px rise, and Raz
     rejected exactly that: "Animation does not match the entrance design you
     set on desktop from bottom to top. It should be right to left (texts).
     Logo from left to right."
     So the container (pager + texts + CTA — the spec lists them as one group)
     now sweeps in from the inline-START edge (right in RTL) while the
     decorative X comes from the inline-END edge. --scrub defaults to 1, so
     no-JS renders the resting state. */
  transform: translateX(
    calc(var(--edge, 1) * min(22vw, 420px) * (1 - var(--scrub, 1)))
  );

  @media (min-width: ${breakpoints.lg}px) {
    flex: 1;
    min-width: 0;
    width: auto;
  }

  @media (prefers-reduced-motion: reduce) {
    transform: none;
  }
`

export const TitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${sizes.spacing[16]}px;
  width: 100%;
`

/* EGDE-504 point 4 ("Texts should change with a circular motion from bottom to
   top within the template", QA 2026-08-26: "Raz talked about having text change
   with motion but we don't have something like that").

   The band's copy is read off the ACTIVE slide, so a slide change simply
   re-rendered different strings into the same nodes — the text popped, with no
   motion of any kind. Measured off Raz's prototype recording (attachment
   183085, frames at 20fps over the "טיולים למוסדות חינוך" → "הפקות אירועים"
   change): each text element clips at its own box, the outgoing line travels UP
   and out of it while the incoming line travels UP into it from below, both on
   screen at once with a gap between them, settling in ~600–700ms. No fade.

   That is the ticker the hero carousel already ships (EGDE-497 r2, accepted for
   the same complaint on the hero title), so the band borrows it rather than
   inventing a second vocabulary for the same gesture. Every slide's copy is
   rendered and the roll is driven by $active.

   Raz 2026-08-27: "Button: stays in its position without motion." The button
   sits below this copy, so the copy boxes cannot change height between slides
   — the ACTIVE-line-sizes-the-wrapper layout the hero uses moved the CTA by
   the difference whenever consecutive descriptions wrapped differently
   (measured 14px on the live content). Every line therefore shares ONE grid
   cell and the box holds the TALLEST line's height on every slide; lines
   stretch to the cell so the ±100% travel always clears the clip box even for
   a line shorter than its tallest sibling. The hero's dead-gap concern
   (EGDE-497) does not transfer: the band's height is fixed by Root's
   aspect-ratio, so the reserved height redistributes inside it. */
/* The line travels its own height plus the clip box's bleed, so it always ends
   fully outside the box even though the box is deliberately bigger than the
   line (see textRollClip). The extra 6px keeps it clear rather than exactly on
   the boundary. */
const ROLL_TRAVEL = "calc(100% + var(--roll-bleed) + 6px)"

const textRollIn = keyframes`
  from {
    transform: translateY(${ROLL_TRAVEL});
  }
  to {
    transform: translateY(0);
  }
`

const textRollOut = keyframes`
  from {
    transform: translateY(0);
  }
  to {
    transform: translateY(calc(-1 * (${ROLL_TRAVEL})));
  }
`

/* 0.7s matches both the prototype's measured settle and the 800ms Swiper travel
   the roll is synced to, so the copy lands just before the photograph does. */
const textRollEase = "cubic-bezier(0.33, 1, 0.68, 1)"
const TEXT_ROLL_DURATION = "0.7s"

/* The outgoing line stays fully opaque for its whole exit and then snaps
   hidden; the incoming one is visible from its first frame. Inactive lines get
   no transition on the initial paint, so nothing flashes on mount. */
const textRoll = (active: boolean) => css`
  /* All lines share the clip box's single cell (see the note above textRollIn)
     — the box is as tall as its tallest line on every slide, so nothing below
     it moves when the copy changes. */
  grid-area: 1 / 1;
  opacity: ${active ? 1 : 0};
  transition: opacity 0s linear ${active ? "0s" : TEXT_ROLL_DURATION};
  /* EGDE-504 (r6, Raz 2026-08-31: "the font weight changed after the
     development fix"). The roll used to keep fill-mode "both" on the ACTIVE
     line too, so a settled line held a translateY(0) transform forever — a
     transformed element stays on its own compositing layer, and composited
     text loses subpixel antialiasing (Windows ClearType renders it grayscale,
     which reads as a lighter weight; the weight itself never changed). The
     incoming animation starts at once and ends at the line's natural resting
     position, so it needs no fill mode at all: when it finishes the transform
     is gone and the glyphs rasterise on the page like any static text. The
     OUTGOING line keeps "both" — it must hold its off-box position until the
     delayed opacity snap hides it — and it is invisible by then, so its
     retained layer can't tint anything. The fill mode is a LONGHAND after the
     shorthand (which then owns the value): a literal "none" inside the
     animation shorthand would parse as an animation-name and void the whole
     declaration. */
  animation: ${active ? textRollIn : textRollOut} ${TEXT_ROLL_DURATION}
    ${textRollEase};
  animation-fill-mode: ${active ? "none" : "both"};

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    transition: none;
  }
`

/* Clip box for a rolling text element — the roll is only a roll because it is
   cut off at the line box.

   It must NOT cut at exactly the line box, though. The desktop h2 is 48px/48px,
   a line-height equal to its font-size, so the glyphs already overflow their own
   line box: measured, the text's content area runs from -9px to +56px inside a
   48px box, and the live title "אגד פלוס חוויות ותוכן" ends in a final nun whose
   tail sits ~4px below 48. `overflow: hidden` sheared it off. (The hero's
   equivalent wrapper is safe only because its h1 is 56/64.) The same cut takes
   the focus ring off the description's rich-text links (EGDE-542), which is a
   keyboard-visibility failure, not just a cosmetic one.

   `overflow-clip-margin` moves the cut outwards by the bleed without changing
   any geometry — no padding, so the absolutely positioned inactive lines still
   sit correctly at `top: 0`, and no negative margin to cancel. Where it is not
   supported `overflow: clip` still cuts at the box, i.e. no worse than the
   value it replaces. */
const ROLL_BLEED = "10px"

const textRollClip = css`
  position: relative;
  width: 100%;
  /* One shared cell — see textRoll. Grid (not flex) because every line must
     occupy the SAME cell so the box takes the tallest line's height. */
  display: grid;
  --roll-bleed: ${ROLL_BLEED};
  overflow: clip;
  overflow-clip-margin: var(--roll-bleed);
`

export const TitleTextWrapper = styled.div`
  ${textRollClip}
`

export const TitleText = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "$active",
})<{ $active: boolean }>`
  color: ${colors.primitives.neutral0};
  text-align: start;
  width: 100%;
  ${(p) => textRoll(p.$active)}
`

export const DescriptionTextWrapper = styled.div`
  ${textRollClip}
`

export const DescriptionText = styled("div", {
  shouldForwardProp: (prop) => prop !== "$active",
})<{ $active: boolean }>`
  color: ${colors.primitives.neutral0};
  text-align: start;
  width: 100%;
  font-size: 18px;
  line-height: 22px;
  letter-spacing: 0.15px;
  ${(p) => textRoll(p.$active)}

  p {
    margin: 0;
  }

  /* EGDE-542: rich-text hyperlinks follow the site-wide link convention
     (EGDE-327) so they read as links against the photo background. */
  & a {
    color: ${colors.primitives.linkBlue} !important;
    text-decoration: underline;
  }

  /* EGDE-541: chevron bullets / orange ordered markers, same as every other
     rich-text renderer. */
  ${richTextListStyles}

  @media (min-width: ${breakpoints.lg}px) {
    font-size: 20px;
    line-height: 28px;
  }
`

const ctaStyles = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 200px;
  padding: ${sizes.spacing[12]}px ${sizes.spacing[16]}px;
  border-radius: ${sizes.borderRadius.full}px;
  color: ${colors.primitives.neutral0};
  font-weight: 700;
  font-size: 18px;
  line-height: 22px;
  letter-spacing: 0.15px;
  text-decoration: none;
  cursor: pointer;
  transition: color 0.2s ease;
  background: rgba(0, 0, 0, 0.4);
  border: 2px solid transparent;
  background-clip: padding-box;
  position: relative;

  /* Gradient border via mask composite */
  &::before {
    content: "";
    position: absolute;
    inset: -2px;
    border-radius: ${sizes.borderRadius.full}px;
    padding: 2px;
    background: ${colors.gradients.gradientCta};
    /* @noflip */
    -webkit-mask:
      linear-gradient(#fff 0 0) content-box,
      linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask:
      linear-gradient(#fff 0 0) content-box,
      linear-gradient(#fff 0 0);
    mask-composite: exclude;
    pointer-events: none;
  }

  /* EGDE-500: Main CTA sheet, hero-section secondary — hover fills with the
     CTA gradient and the label darkens; press goes solid orange. The
     gradient swap is instant (cf. EGDE-355). */
  &:hover,
  &:focus-visible {
    background: ${colors.gradients.gradientCta};
    background-clip: padding-box;
    color: ${colors.light.text.primary};
  }

  &:active {
    background: ${colors.primitives.primary500};
    background-clip: padding-box;
    color: ${colors.light.text.primary};
  }

  /* EGDE-510 point 8, MOBILE: the spec splits this breakpoint — "Play button
     and figure + texts enter from right to left. Button enters from bottom to
     top." The CTA lives inside ContentContainer, which sweeps horizontally, so
     the first translate CANCELS the parent's travel exactly (same expression,
     opposite sign — nested transforms compose) and the second supplies the
     rise. Without the cancellation the button would arrive diagonally.
     Desktop keeps the CTA in the container's single group per the spec. */
  transform: translateX(
      calc(-1 * var(--edge, 1) * min(22vw, 420px) * (1 - var(--scrub, 1)))
    )
    translateY(calc(32px * (1 - var(--scrub, 1))));

  @media (min-width: ${breakpoints.lg}px) {
    font-size: 20px;
    line-height: 28px;
    transform: none;
  }

  @media (prefers-reduced-motion: reduce) {
    transform: none;
  }
`

export const CtaButton = styled.a`
  ${ctaStyles}
`

export const CtaLink = styled(Link)`
  ${ctaStyles}
`

/* EGDE-504 (Raz 2026-08-27): "Button: stays in its position without motion.
   Only the texts within the button change." When slides carry DIFFERENT CTAs
   the band keeps ONE stationary shell — the same pill as CtaButton/CtaLink —
   and rolls only the label inside it. $hidden covers a slide with no CTA at
   all: the shell fades in place rather than unmounting, because a remount
   would restart the label roll mid-flight and a disappearing button is
   motion. */
const ctaShell = (hidden: boolean) => css`
  ${ctaStyles}
  transition:
    color 0.2s ease,
    opacity 0.3s ease,
    visibility 0s linear ${hidden ? "0.3s" : "0s"};
  opacity: ${hidden ? 0 : 1};
  pointer-events: ${hidden ? "none" : "auto"};
  /* An invisible link must leave the tab order too — visibility (delayed past
     the fade when hiding, instant when showing) does what opacity cannot. */
  visibility: ${hidden ? "hidden" : "visible"};
`

export const CtaShellAnchor = styled("a", {
  shouldForwardProp: (prop) => prop !== "$hidden",
})<{ $hidden: boolean }>`
  ${(p) => ctaShell(p.$hidden)}
`

export const CtaShellLink = styled(Link, {
  shouldForwardProp: (prop) => prop !== "$hidden",
})<{ $hidden: boolean }>`
  ${(p) => ctaShell(p.$hidden)}
`

/* The label's own clip box. All labels share one grid cell, so the shell is
   sized by its WIDEST label and a longer incoming label cannot resize — i.e.
   move — the button's edges. The bleed keeps the roll from shaving descenders
   (same trick as textRollClip); 6px stays inside the pill's 12px vertical
   padding, so the roll never paints outside the button. */
export const CtaLabelStack = styled.span`
  display: grid;
  --roll-bleed: 6px;
  overflow: clip;
  overflow-clip-margin: var(--roll-bleed);
  text-align: center;
`

export const CtaLabelLine = styled("span", {
  shouldForwardProp: (prop) => prop !== "$active",
})<{ $active: boolean }>`
  ${(p) => textRoll(p.$active)}
`

export const CarouselNavContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${sizes.spacing[12]}px;
`

export const CarouselNavBars = styled.div`
  display: flex;
  align-items: center;
`

/* EGDE-530: each bar is a button that jumps to its slide; the padded button
   keeps the 28×4 visual track while giving a usable hit area (the 4px
   horizontal padding replaces the previous 8px flex gap). */
export const NavBar = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  margin: 0;
  padding: ${sizes.spacing[8]}px ${sizes.spacing[4]}px;
  cursor: pointer;
`

export const NavBarTrack = styled.div`
  width: 28px;
  height: 4px;
  border-radius: ${sizes.borderRadius.full}px;
  background-color: rgba(158, 158, 158, 0.5);
  position: relative;
  overflow: hidden;
`

export const NavBarProgress = styled.div<{
  $state: NavBarState
  $duration: number
}>`
  position: absolute;
  inset: 0;
  border-radius: inherit;
  background-color: ${colors.primitives.neutral0};
  /* EGDE-504: no @noflip — stylis-rtl flips this to "right" in RTL so the
     fill grows in reading direction, matching the prototype (prod builds
     strip @noflip comments, so relying on one splits dev from prod). */
  transform-origin: left;
  transform: scaleX(${(p) => (p.$state === "idle" ? 0 : 1)});
  ${(p) =>
    p.$state === "active"
      ? css`
          animation: ${fillProgress} ${p.$duration}ms linear forwards;
        `
      : ""}
`

/* EGDE-504: circle button rests as the dark translucent on-image disc with a
   white glyph; the Figma circle-button sheet pairs it with a hover state that
   flips to a white disc with a light border and the orange glyph. */
export const CircleIconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: ${sizes.borderRadius.full}px;
  background-color: rgba(0, 0, 0, 0.5);
  border: 1px solid ${colors.primitives.neutral500};
  backdrop-filter: blur(4px);
  color: ${colors.primitives.neutral0};
  cursor: pointer;
  flex-shrink: 0;
  padding: 0;
  transition:
    background-color 0.2s ease,
    border-color 0.2s ease,
    color 0.2s ease;

  &:hover {
    background-color: ${colors.primitives.neutral0};
    border-color: ${colors.primitives.neutral200};
    color: ${colors.primitives.primary500};
  }
`
